import { Autocomplete, Checkbox, Chip } from "@mui/material";
import TextFieldMUI from "@mui/material/TextField";
import { downloadDetailReport, downloadSummaryReport } from "api";
import axios from "axios";
import { Button } from "baseui/button";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { Col, Row } from "react-flexbox-grid";
import { saveFile } from "utils/download";

import * as S from "./style";

axios.defaults.headers.common["token"] = "3abd7f83-aae7-48fd-a4e5-77f19689921a";

const Downloads = ({ canDownloadHotelList }) => {
  const [selectedDate, setSelectedDate] = useState("today");
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const responseType = "0";
  const defaultHotelList = canDownloadHotelList;
  const [hotelList, setHotelList] = useState(defaultHotelList);

  useEffect(() => {
    setHotelList(canDownloadHotelList || []);
  }, [canDownloadHotelList]);

  const onDownloadSummaryReportClick = async () => {
    try {
      const fileName = `summary-report-${dayjs().format(
        "YYYY-MM-DD HH:mm:ss"
      )}.csv`;
      const response = await downloadSummaryReport(
        startDate,
        endDate,
        responseType,
        hotelList.map((hotel) => hotel.hotelId)
      );
      saveFile(response, fileName);
    } catch (e) {
      console.log(e);
    }
  };

  const onDownloadDetailReportClick = async () => {
    try {
      const fileName = `detail-report-${dayjs().format(
        "YYYY-MM-DD HH:mm:ss"
      )}.csv`;
      const response = await downloadDetailReport(
        startDate,
        endDate,
        responseType,
        hotelList.map((hotel) => hotel.hotelId)
      );
      saveFile(response, fileName);
    } catch (e) {
      console.log(e);
    }
  };

  const onDatePeriodSelect = (datePeriod) => {
    switch (datePeriod) {
      case "yesterday":
        setSelectedDate(datePeriod);
        setStartDate(dayjs().subtract(1, "days").toDate());
        setEndDate(dayjs().subtract(1, "days").toDate());
        break;
      case "last7days":
        setSelectedDate(datePeriod);
        setStartDate(() => dayjs().subtract(1, "weeks").add(1, "day").toDate());
        setEndDate(new Date());
        break;
      case "last30days":
        setSelectedDate(datePeriod);
        setStartDate(() => dayjs().subtract(1, "months").toDate());
        setEndDate(new Date());
        break;
      case "today":
      default:
        setSelectedDate(datePeriod);
        setStartDate(new Date());
        setEndDate(new Date());
    }
  };

  return (
    <S.Download>
      <S.Panel>
        <header>
          <S.DatePeriodSelect>
            <S.DatePeriod
              selected={selectedDate === "today"}
              onClick={() => onDatePeriodSelect("today")}
            >
              Today
            </S.DatePeriod>
            <S.DatePeriod
              selected={selectedDate === "yesterday"}
              onClick={() => onDatePeriodSelect("yesterday")}
            >
              Yesterday
            </S.DatePeriod>
            <S.DatePeriod
              selected={selectedDate === "last7days"}
              onClick={() => onDatePeriodSelect("last7days")}
            >
              Last 7 Days
            </S.DatePeriod>
            <S.DatePeriod
              selected={selectedDate === "last30days"}
              onClick={() => onDatePeriodSelect("last30days")}
            >
              Last 30 Days
            </S.DatePeriod>
            <S.DatePeriod selected={selectedDate === "customPeriod"}>
              Custom period
            </S.DatePeriod>
          </S.DatePeriodSelect>
          <S.DateRangeSelectContainer>
            <div className="text">From</div>
            <div>
              <DatePicker
                className="date-picker"
                selected={startDate}
                onChange={(date) => {
                  setStartDate(date);
                  setSelectedDate("customPeriod");
                }}
              />
            </div>
            <div className="text">To</div>
            <div>
              <DatePicker
                className="date-picker"
                selected={endDate}
                onChange={(date) => {
                  setEndDate(date);
                  setSelectedDate("customPeriod");
                }}
              />
            </div>
          </S.DateRangeSelectContainer>
        </header>
        {canDownloadHotelList && canDownloadHotelList.length > 1 && (
          <S.HotelSelectContainer>
            <Autocomplete
              multiple
              options={defaultHotelList || []}
              disableClearable
              disableCloseOnSelect
              value={hotelList}
              isOptionEqualToValue={(option, value) =>
                option.hotelId === value.hotelId
              }
              onChange={(event, newValue) => {
                if (newValue.length === 0) return;
                setHotelList(newValue);
              }}
              getOptionLabel={(option) => option.hotelName}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const tagProps = getTagProps({ index });

                  return (
                    <Chip
                      {...tagProps}
                      key={option.hotelId}
                      label={option.hotelName}
                      onDelete={
                        value.length > 1 ? tagProps.onDelete : undefined
                      }
                    />
                  );
                })
              }
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox checked={selected} />
                  {option.hotelName}
                </li>
              )}
              renderInput={(params) => (
                <TextFieldMUI
                  {...params}
                  label="Hotel"
                  variant="standard"
                  fullWidth
                />
              )}
            />
          </S.HotelSelectContainer>
        )}
        <main>
          <Col md={9} xs={12}>
            <Row>
              <Col md={4} xs={12}>
                <Button
                  onClick={onDownloadSummaryReportClick}
                  overrides={{
                    BaseButton: {
                      style: () => ({
                        width: "100%",
                        borderTopLeftRadius: "3px",
                        borderTopRightRadius: "3px",
                        borderBottomLeftRadius: "3px",
                        borderBottomRightRadius: "3px",
                      }),
                    },
                  }}
                >
                  Download Summary Report
                </Button>
              </Col>
              <Col md={4} xs={12}>
                <Button
                  onClick={onDownloadDetailReportClick}
                  overrides={{
                    BaseButton: {
                      style: () => ({
                        width: "100%",
                        borderTopLeftRadius: "3px",
                        borderTopRightRadius: "3px",
                        borderBottomLeftRadius: "3px",
                        borderBottomRightRadius: "3px",
                      }),
                    },
                  }}
                >
                  Download Detail Report
                </Button>
              </Col>
            </Row>
          </Col>
        </main>
      </S.Panel>
    </S.Download>
  );
};

export default Downloads;
